// Planning popup — the ArchViz Planner's per-project screen, recreated as a
// popup on the project page instead of a separate module. One flow, and the
// planner's is the source of truth: Parse Agreement / Recalc opens
// **Confirm Scope** (quantities only), which hands off to the **timeline
// breakdown** step (artists, prereqs, scope table, phases, milestones) before
// anything is written. The body below mirrors the planner's "Active Projects"
// layout: scope breakdown, Project Timeline Gantt, Production Timeline,
// Payments.
//
// The Payments module here is the SAME component as the Payment Schedule tab
// (MilestonePaymentsTab), embedded — full functionality, one implementation,
// so an edit in either place is the same edit. It reports data changes up via
// onChanged so the Project Timeline's milestone row stays in sync.
//
// Every number in the breakdown step comes from ONE backend call
// (`POST /archviz/scope-preview/{id}`) that runs the exact function
// `apply-scope` writes with. The planner computed some values on input and left
// the phase list rendered from the first pass, so its modal could show 3 weeks
// next to 14 working days of modeling; that can't happen here.
//
// The "Project Timeline" is NOT a second data store — it's a read-only view of
// the same `pcc_program_phases` rows the Production Timeline edits, plus a
// milestone overlay from the program's real payment schedule. Two Gantts backed
// by two independently-editable copies of the same schedule is exactly the sync
// bug the old planner kept shipping.
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import {
  Loader2, ZoomIn, ZoomOut, Plus, Check, Trash2, FileWarning, Clock,
  CalendarRange, Diamond, Sparkles, RotateCw, SlidersHorizontal, CalendarClock,
  ChevronRight, AlertTriangle,
} from "lucide-react";
import { ProgramSection, sectionActionClass } from "./ProgramSection";
import MilestonePaymentsTab from "./MilestonePaymentsTab";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const DAY_MS = 24 * 60 * 60 * 1000;
const PHASE_COLORS = ["#3D6355", "#8FA79B", "#B96A43", "#2E7D55", "#C24A2E"];
const DELIV_COLORS = ["#3D6355", "#8FA79B", "#B96A43", "#2E7D55", "#C24A2E", "#8A8172"];
const KICKOFF_COLOR = "#5E8EFF";
const BUFFER_COLOR = "#8A8172";
// Production Timeline's label column: checkbox + name + done + delete only.
// Tags and artist assignment deliberately do NOT live on these rows — the
// Artist Timeline owns assignment, and the dropdowns crowded the name into
// "Kickoff…", "3D Mo…", "Exterio…".
const PROD_LABEL_W = 260;

function isoDate(d) { return new Date(d).toISOString().slice(0, 10); }
// UTC-based on purpose: every date here is a date-only value (UTC midnight),
// and local setDate() arithmetic can slide the UTC day across a DST boundary.
function addDays(date, n) {
  const d = new Date(date);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n));
}
// Phase dates are date-only strings, so `new Date("2026-08-05")` is UTC
// midnight. Formatting that in a negative-offset local zone renders the
// PREVIOUS day — a kickoff of 08-05 showed as "Aug 4". Everything here reads
// the UTC calendar day, which is the day the string actually names.
function fmtShort(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }); }
  catch { return ""; }
}
/** Midnight UTC of the same calendar day, as a timestamp. */
function utcDayStart(d) {
  const dt = new Date(d);
  return Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
}
/** Two-tier ruler scale, in the same UTC day-grid the bars are placed on.
 * Month bands read "AUG 2026" — the old single-row "Aug 26" label was
 * indistinguishable from a day-of-month. Underneath, day-of-month tick marks:
 * every Monday, or every day once the zoom leaves room for the labels. */
function utcRulerScale(startMs, days, px) {
  const endMs = startMs + days * DAY_MS;
  const months = [];
  const s = new Date(startMs);
  let cursor = Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), 1);
  while (cursor < endMs) {
    const c = new Date(cursor);
    const next = Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + 1, 1);
    const from = Math.max(cursor, startMs);
    months.push({
      label: c.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" }).toUpperCase(),
      shortLabel: c.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }).toUpperCase(),
      offset: Math.round((from - startMs) / DAY_MS),
      span: Math.round((Math.min(next, endMs) - from) / DAY_MS),
      // The first band usually starts mid-month — its left edge is the range
      // edge, not the 1st, so it gets no boundary line.
      isMonthStart: cursor >= startMs,
    });
    cursor = next;
  }
  const labelEveryDay = px >= 18;
  const dayTicks = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(startMs + i * DAY_MS);
    if (labelEveryDay || d.getUTCDay() === 1) {
      dayTicks.push({ offset: i, label: String(d.getUTCDate()) });
    }
  }
  return { months, dayTicks };
}

const RULER_H = 40;
/** The date scale both Gantts share: month bands over day ticks + TODAY. */
function GanttRuler({ scale, px, todayOffset, todayVisible }) {
  return (
    <div className="relative h-full">
      {scale.months.map((m, i) => (
        <div
          key={i}
          className={`absolute top-0 overflow-hidden whitespace-nowrap pl-1.5 pt-1 text-[10px] font-mono font-semibold tracking-wide text-muted-foreground ${m.isMonthStart ? "border-l border-border" : ""}`}
          style={{ left: m.offset * px, width: m.span * px, height: 20 }}
        >
          {m.span * px >= 70 ? m.label : m.shortLabel}
        </div>
      ))}
      {scale.dayTicks.map((t, i) => (
        <div
          key={i}
          className="absolute bottom-0 border-l border-border/50 pl-1 pt-0.5 text-[9px] font-mono text-muted-foreground"
          style={{ left: t.offset * px, height: 19 }}
        >
          {t.label}
        </div>
      ))}
      {todayVisible && (
        <div className="absolute top-0 bottom-0 z-10 w-0.5" style={{ left: todayOffset, background: "#C24A2E" }}>
          <span className="absolute left-1 top-1 whitespace-nowrap text-[10px] font-mono font-bold" style={{ color: "#C24A2E" }}>TODAY</span>
        </div>
      )}
    </div>
  );
}
/** Vertical gridlines matching the ruler — month boundaries strong, day/week
 * ticks faint — so a bar visibly sits on its real dates. */
function GanttGridlines({ scale, px }) {
  return (
    <>
      {scale.dayTicks.map((t, i) => (
        <div key={`d${i}`} className="absolute top-0 bottom-0 border-l border-border/25" style={{ left: t.offset * px }} />
      ))}
      {scale.months.filter(m => m.isMonthStart).map((m, i) => (
        <div key={`m${i}`} className="absolute top-0 bottom-0 border-l border-border/60" style={{ left: m.offset * px }} />
      ))}
    </>
  );
}

// Deliverables render grouped in a fixed track order: 3D Modeling → Exterior →
// Interior → Vexos (Kickoff/Buffer prereqs stay in front of all of them).
// apply-scope historically wrote the modeling section as track_type "custom",
// so the label is the fallback signal for data already out there.
const TRACK_ORDER = { modeling: 0, exterior: 1, interior: 2, vexos: 3 };
function sectionRank(section) {
  if (!section) return 90;
  const t = (section.track_type || "").toLowerCase();
  if (t in TRACK_ORDER) return TRACK_ORDER[t];
  const label = (section.label || "").toLowerCase();
  if (/model/.test(label)) return 0;
  if (/exterior/.test(label)) return 1;
  if (/interior/.test(label)) return 2;
  if (/vexos|animation/.test(label)) return 3;
  return 4;
}
function fmtMoney(n) {
  return "$" + Math.round(Number(n) || 0).toLocaleString();
}
// Quantities and day counts come back as JSON floats, so a whole number
// arrives as 5.0 — show "5 images", not "5.0 images".
function fmtNum(n) {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 100) / 100);
}

// ── Quantity groups for Confirm Scope — the planner's own grouping, order and
// field labels (showExtractionReview), so PMs see the categories they know. ──
const QTY_GROUPS = [
  { label: "Exterior Renders", color: "#B96A43", fields: [
    ["extHeroDay", "Hero Day Renders"], ["extHeroNight", "Hero Night Renders"],
    ["extAmenities", "Amenity Renders"], ["extMasterplan", "Masterplan Render"],
  ]},
  { label: "Interior Renders", color: "#8FA79B", fields: [
    ["intBedroom", "Interior Renders (generic/bedroom)"], ["intLivingSpace", "Living Space S/M"],
    ["intLivingSpaceL", "Living Space L"], ["intBathroom", "Bathroom"],
    ["intAmenities", "Interior Amenity"], ["intMasterplan", "Interior Masterplan"],
  ]},
  { label: "3D Modeling", color: "#3D6355", fields: [
    ["modeling_house", "House / Residential"], ["modeling_midlevel", "Mid-Rise Building"],
    ["modeling_highrise", "High-Rise Building"], ["modeling_units", "Individual Units"],
    ["modeling_masterplan", "Masterplan Model"], ["modeling_furnishing", "Furnishing Package"],
    ["modeling_landscape", "Landscape"],
  ]},
  { label: "Unit VR Tours", color: "#5E8EFF", fields: [
    ["unitVR_4pt", "4-Point Tours"], ["unitVR_6pt", "6-Point Tours"], ["unitVR_8pt", "8-Point Tours"],
    ["unitVR_10pt", "10-Point Tours"], ["unitVR_12pt", "12-Point Tours"],
  ]},
  { label: "Amenity VR Tours", color: "#D0956C", fields: [
    ["amenityVR_4pt", "4-Point Tours"], ["amenityVR_6pt", "6-Point Tours"], ["amenityVR_8pt", "8-Point Tours"],
    ["amenityVR_10pt", "10-Point Tours"], ["amenityVR_12pt", "12-Point Tours"],
  ]},
  { label: "Animation", color: "#2E7D55", fields: [
    ["anim30", "30-Second Videos"], ["anim60", "60-Second Videos"],
    ["anim90", "90-Second Videos"], ["anim120", "120-Second Videos"],
  ]},
  { label: "Vexos", color: "#C24A2E", fields: [
    ["vexos_orbit", "Orbit (always 1 per contract)"], ["vexos_keyframe", "Keyframes in Orbit"],
    ["vexos_animatedKeyframe", "Animated Keyframes"],
  ]},
  { label: "Floorplans", color: "#8A8172", fields: [
    ["floorplan_ai", "AI Floorplans (default)"], ["floorplan_3d", "3D Floorplans"],
    ["floorplan_2d", "2D Marketing Floorplans"],
  ]},
];
const ALL_QTY_KEYS = QTY_GROUPS.flatMap(g => g.fields.map(([k]) => k));

// Modeling working days when the calculator priced none — the planner's own
// default, and the reason the breakdown step makes the field editable.
const DEFAULT_MODELING_WD = 14;

export default function ScopeTimelinePlanningPopup({ programId, programName, canViewFinancials }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [phases, setPhases] = useState([]);
  const [sections, setSections] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [planningInputs, setPlanningInputs] = useState(null);
  const [quoteScope, setQuoteScope] = useState(null);
  const [flow, setFlow] = useState(null); // null | {stage:'scope'|'breakdown', ...}
  // Bumped when a popup-side flow rewrites the payment schedule, so the
  // embedded Payments module refetches. The module pushes fresh data back up
  // through adoptPayments, keeping the timeline's milestone row in sync.
  const [payVersion, setPayVersion] = useState(0);

  const adoptPayments = useCallback((data) => {
    setMilestones(data?.milestones || []);
  }, []);

  const refreshPayments = useCallback(async () => {
    try {
      const res = await apiClient.get(`/programs/${programId}/payments/schedule`);
      setMilestones(res.data?.milestones || []);
    } catch {
      setMilestones([]);
    }
  }, [programId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [phRes, secRes] = await Promise.all([
        apiClient.get(`/pcc/programs/${programId}/phases`),
        apiClient.get(`/pcc/programs/${programId}/sections`),
      ]);
      setPhases(phRes.data || []);
      setSections(secRes.data || []);
    } catch {
      toast.error("Couldn't load the planning timeline");
    } finally {
      setLoading(false);
    }
    apiClient.get(`/archviz/planning-inputs/${programId}`)
      .then(r => setPlanningInputs(r.data || {}))
      .catch(() => setPlanningInputs({}));
    refreshPayments();
  }, [programId, refreshPayments]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="open-planning-popup">
        <CalendarRange className="w-3.5 h-3.5 mr-1.5" />
        Planning
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto" data-testid="planning-popup">
          <DialogHeader>
            <DialogTitle className="text-xl font-light">Planning — {programName}</DialogTitle>
            <DialogDescription>Scope, contract breakdown, and the production schedule for this project.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <ScopeOfWorkPanel
              programId={programId}
              canViewFinancials={canViewFinancials}
              onScopeLoaded={setQuoteScope}
              onParse={(scope) => setFlow({ stage: "scope", scope, source: "quote" })}
            />
            <ProjectTimelineGantt
              phases={phases}
              sections={sections}
              milestones={milestones}
              artists={planningInputs?.artists_per_task}
              canViewFinancials={canViewFinancials}
              loading={loading}
              onShift={async (deltaDays) => {
                await Promise.all(phases.map(p =>
                  apiClient.patch(`/pcc/phases/${p.id}`, { start_date: isoDate(addDays(p.start_date, deltaDays)) })
                ));
                refresh();
              }}
            />
            <ProductionTimelinePanel
              programId={programId}
              phases={phases}
              sections={sections}
              loading={loading}
              onChange={setPhases}
              onRefresh={refresh}
              canRecalc={canViewFinancials}
              // The quote goes along as a fallback: a project whose schedule was
              // migrated from the old planner has phases but no stored inputs,
              // and opening Recalc on an all-zero scope would be useless.
              onRecalc={() => setFlow({ stage: "scope", scope: quoteScope, source: "recalc", inputs: planningInputs })}
            />
            {canViewFinancials && (
              <MilestonePaymentsTab
                programId={programId}
                onChanged={adoptPayments}
                refreshToken={payVersion}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {flow?.stage === "scope" && (
        <ConfirmScopeDialog
          scope={flow.scope}
          inputs={flow.inputs}
          onClose={() => setFlow(null)}
          onNext={(quantities) => setFlow({ ...flow, stage: "breakdown", quantities })}
        />
      )}
      {flow?.stage === "breakdown" && (
        <TimelineBreakdownDialog
          programId={programId}
          quantities={flow.quantities}
          scope={flow.scope}
          inputs={flow.inputs}
          milestones={milestones}
          onBack={() => setFlow({ ...flow, stage: "scope" })}
          onClose={() => setFlow(null)}
          onApplied={() => { setFlow(null); refresh(); setPayVersion(v => v + 1); }}
        />
      )}
    </>
  );
}

// ── Scope of Work — the whole scope breakdown at the top of the popup, plus
// the Parse Agreement entry into the two-step flow. ──────────────────────
function ScopeOfWorkPanel({ programId, canViewFinancials, onParse, onScopeLoaded }) {
  const [state, setState] = useState({ loading: true, scope: null, error: null, fallback: null });

  useEffect(() => {
    if (!canViewFinancials) { setState({ loading: false, scope: null, error: null, fallback: null }); return; }
    let alive = true;
    (async () => {
      try {
        const res = await apiClient.get(`/archviz/quote-scope/${programId}`);
        if (alive) { setState({ loading: false, scope: res.data, error: null, fallback: null }); onScopeLoaded?.(res.data); }
      } catch (e) {
        if (!alive) return;
        const detail = e?.response?.data?.detail;
        const message = typeof detail === "object" ? detail.message : detail;
        setState({
          loading: false, scope: null,
          error: message || "Couldn't load scope",
          fallback: typeof detail === "object" ? detail.fallback : null,
        });
      }
    })();
    return () => { alive = false; };
  }, [programId, canViewFinancials, onScopeLoaded]);

  if (!canViewFinancials) return null;

  // Everything sold belongs in the scope list — including add-ons the planner
  // has no quantity field for (additional orbit, day-to-night, drone capture).
  // Those render with a hollow dot + tooltip instead of being hidden, since a
  // chip that silently vanishes reads as "not in the contract".
  const items = (state.scope?.rawDeliverables || []).filter(d => !d.ignored);

  return (
    <ProgramSection
      title="Scope of Work"
      action={
        <Button size="sm" onClick={() => onParse(state.scope)} className={sectionActionClass} data-testid="open-parse-agreement">
          <Sparkles className="w-3.5 h-3.5 mr-1" />
          Parse Agreement
        </Button>
      }
    >
      {state.loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading scope…
        </div>
      ) : !state.scope ? (
        <div className="flex items-start gap-2.5 text-[13px] text-muted-foreground">
          <FileWarning className="w-4 h-4 mt-0.5 shrink-0 text-[#B96A43]" />
          <span>
            {state.error}
            {state.fallback === "pdf" && " — click Parse Agreement to enter the scope by hand."}
          </span>
        </div>
      ) : items.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">No priced production scope on the linked quote yet.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((d, i) => {
            const unplanned = d.mapped === false;
            // The backend suffixes unmapped raws with planner-flow guidance
            // ("— no planner field, add days manually"); as a chip that reads
            // like part of the product name, so it moves to the tooltip.
            const name = unplanned ? d.name.replace(/\s+—\s+no planner field.*$/i, "") : d.name;
            return (
              <div
                key={i}
                className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-[13px]"
                title={unplanned ? "Sold on the quote, but the planner has no field for it — account for its production days by hand." : undefined}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={unplanned
                    ? { background: "transparent", boxShadow: `inset 0 0 0 1.5px ${DELIV_COLORS[i % DELIV_COLORS.length]}` }
                    : { background: DELIV_COLORS[i % DELIV_COLORS.length] }}
                />
                <span className="font-medium text-foreground">{d.qty ? `${d.qty}× ` : ""}{name}</span>
              </div>
            );
          })}
        </div>
      )}
    </ProgramSection>
  );
}

// ── Step 1: Confirm Scope ────────────────────────────────────────────────
// Quantities only. No project name/client/contract-value fields (HQ owns
// those on its own tabs) and no quote line-item list — the scope chips in the
// panel above already show what was sold.
function ConfirmScopeDialog({ scope, inputs, onClose, onNext }) {
  const initial = useMemo(() => {
    const src = inputs?.quantities || scope?.quantities || {};
    const out = {};
    ALL_QTY_KEYS.forEach(k => { out[k] = Number(src[k]) || 0; });
    return out;
  }, [scope, inputs]);

  const [quantities, setQuantities] = useState(initial);
  const setQty = (key, val) => setQuantities(q => ({ ...q, [key]: Math.max(0, Number(val) || 0) }));

  const extractedTotal = useMemo(() => ALL_QTY_KEYS.reduce((s, k) => s + (initial[k] || 0), 0), [initial]);
  const currentTotal = useMemo(() => ALL_QTY_KEYS.reduce((s, k) => s + (quantities[k] || 0), 0), [quantities]);
  const diff = currentTotal - extractedTotal;

  // Named changes, not just a delta — "−1 Bathroom, +2 Hero Day Renders" is
  // what tells a PM whether they edited the field they meant to.
  const { added, removed } = useMemo(() => {
    const a = [], r = [];
    QTY_GROUPS.forEach(g => g.fields.forEach(([k, label]) => {
      const d = (quantities[k] || 0) - (initial[k] || 0);
      if (d > 0) a.push(`${d} ${label}`);
      if (d < 0) r.push(`${Math.abs(d)} ${label}`);
    }));
    return { added: a, removed: r };
  }, [quantities, initial]);

  const activeGroups = QTY_GROUPS.filter(g => g.fields.some(([k]) => (initial[k] || 0) > 0));
  const emptyGroups = QTY_GROUPS.filter(g => !g.fields.some(([k]) => (initial[k] || 0) > 0));
  const fromQuote = !!scope;
  const modelingNote = scope?.notes
    || "3D modeling isn't priced separately in the calculator — set its working days in the next step.";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="confirm-scope">
        <DialogHeader>
          <DialogTitle>Confirm Scope</DialogTitle>
          <DialogDescription>
            {inputs?.quantities
              ? "The quantities this project's current schedule was built from — edit anything that changed."
              : fromQuote
                ? "Exact scope from the quote this contract was priced from — no PDF reading involved."
                : "Nothing came back from the quote — fill in the quantities by hand."}
          </DialogDescription>
        </DialogHeader>

        {fromQuote && (
          <div className="rounded-lg border border-[#2E7D55]/30 bg-[#2E7D55]/[0.07] px-3.5 py-2.5 text-[12px] text-muted-foreground">
            <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-[#2E7D55]" />
            Adjust anything that changed since the contract was signed.
          </div>
        )}

        <div className="rounded-lg border border-border border-l-2 border-l-[#B96A43] px-3.5 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
          {modelingNote}
        </div>

        {/* Running total tracker */}
        <div
          className="flex flex-wrap items-center justify-between gap-1 rounded-lg border px-3 py-2 text-[11px] transition-colors"
          style={{
            borderColor: diff === 0 ? "" : diff > 0 ? "rgba(46,125,85,.4)" : "rgba(194,74,46,.4)",
            background: diff === 0 ? "" : diff > 0 ? "rgba(46,125,85,.06)" : "rgba(194,74,46,.06)",
          }}
          data-testid="scope-total-banner"
        >
          <span className="text-muted-foreground">
            Extracted total: <b className="text-foreground tabular-nums">{extractedTotal}</b> deliverables
          </span>
          <span className="text-muted-foreground">
            Current:{" "}
            <b
              className="tabular-nums"
              style={{ color: diff === 0 ? "" : diff > 0 ? "#2E7D55" : "#C24A2E" }}
              data-testid="scope-current-total"
            >
              {currentTotal}
            </b>
          </span>
          {diff !== 0 && (
            <div className="w-full text-[10px] leading-relaxed flex flex-wrap gap-x-4">
              {removed.length > 0 && <span style={{ color: "#C24A2E" }}>− {removed.join(", ")}</span>}
              {added.length > 0 && <span style={{ color: "#2E7D55" }}>+ {added.join(", ")}</span>}
            </div>
          )}
        </div>

        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          Extracted quantities — edit anything that's wrong
        </div>

        {activeGroups.length === 0 ? (
          <>
            <div className="rounded-lg border border-dashed border-border px-4 py-4 text-center text-[12px] text-muted-foreground">
              No deliverables extracted — fill in the quantities by hand below.
            </div>
            {QTY_GROUPS.map(g => (
              <QtyGroup key={g.label} group={g} quantities={quantities} setQty={setQty} />
            ))}
          </>
        ) : (
          <>
            {activeGroups.map(g => (
              <QtyGroup key={g.label} group={g} quantities={quantities} setQty={setQty} />
            ))}
            {emptyGroups.length > 0 && (
              <details data-testid="add-missing-deliverables">
                <summary className="cursor-pointer list-none py-1.5 text-[11px] text-muted-foreground hover:text-foreground">
                  + Add missing deliverables
                </summary>
                <div className="mt-2 space-y-2.5">
                  {emptyGroups.map(g => (
                    <QtyGroup key={g.label} group={g} quantities={quantities} setQty={setQty} />
                  ))}
                </div>
              </details>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onNext(quantities)} data-testid="calculate-timeline">
            Calculate Timeline
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QtyGroup({ group, quantities, setQty }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-3 py-1.5">
        <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: group.color }} />
        <span className="text-[11px] font-semibold tracking-wide">{group.label}</span>
      </div>
      {group.fields.map(([key, label]) => {
        const v = quantities[key] || 0;
        return (
          <div key={key} className="flex items-center gap-3 border-b border-border px-3 py-1.5 last:border-b-0">
            <span className="flex-1 text-[11px] text-muted-foreground truncate">{label}</span>
            <Input
              type="number" min={0}
              className="h-7 w-16 text-center tabular-nums text-[13px] font-semibold"
              style={v > 0 ? { borderColor: "rgba(94,142,255,.45)", color: KICKOFF_COLOR } : undefined}
              value={v}
              onChange={(e) => setQty(key, e.target.value)}
              data-testid={`qty-${key}`}
            />
          </div>
        );
      })}
    </div>
  );
}

// ── Step 2: the timeline calculation breakdown ───────────────────────────
function TimelineBreakdownDialog({ programId, quantities, scope, inputs, milestones, onBack, onClose, onApplied }) {
  const [artists, setArtists] = useState(inputs?.artists_per_task || scope?.quantities?.artistsPerTask || 1);
  const [bufferWd, setBufferWd] = useState(
    inputs?.kickoff_buffer_days != null ? inputs.kickoff_buffer_days : 5
  );
  const [kickoffDate, setKickoffDate] = useState(
    inputs?.kickoff_date || scope?.startDate || isoDate(new Date())
  );
  // 3D modeling is never priced by the calculator, so it starts at the
  // planner's default and the PM sets the real figure here.
  const [modelingWd, setModelingWd] = useState(() => {
    if (inputs?.modeling_override_wd != null) return inputs.modeling_override_wd;
    return DEFAULT_MODELING_WD;
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(true);
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [expanded, setExpanded] = useState({});

  const bufferPct = inputs?.buffer_pct != null ? inputs.buffer_pct : 0.10;

  const body = useMemo(() => ({
    quantities,
    artists_per_task: Math.max(1, Number(artists) || 1),
    buffer_pct: bufferPct,
    kickoff_buffer_days: Math.max(0, Number(bufferWd) || 0),
    kickoff_date: kickoffDate,
    modeling_override_wd: Math.max(0, Number(modelingWd) || 0),
  }), [quantities, artists, bufferPct, bufferWd, kickoffDate, modelingWd]);

  // One request per settled edit — every figure below comes from the same
  // calculation apply-scope will run, so preview and result can't disagree.
  useEffect(() => {
    if (!kickoffDate) { setError("Set a kickoff date to calculate the timeline."); setPreviewing(false); return; }
    let alive = true;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const res = await apiClient.post(`/archviz/scope-preview/${programId}`, body);
        if (alive) { setPreview(res.data); setError(null); }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Couldn't calculate the timeline");
      } finally {
        if (alive) setPreviewing(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [programId, body, kickoffDate]);

  async function apply(force) {
    setApplying(true);
    try {
      await apiClient.post(`/archviz/apply-scope/${programId}`, { ...body, force: !!force });
      toast.success(force ? "Timeline replaced" : "Timeline built from scope");
      onApplied();
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail = e.response.data?.detail;
        const message = (typeof detail === "object" ? detail.message : detail)
          || "This project already has a production schedule.";
        if (window.confirm(`${message}\n\nReplace it with the schedule above?`)) {
          setApplying(false);
          return apply(true);
        }
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't build the timeline");
      }
    } finally {
      setApplying(false);
    }
  }

  // Milestones: the quote's own payment plan when this scope came from one,
  // otherwise the program's real schedule. Read-only here — HQ owns the money,
  // and the Payments module in the popup is where dates and amounts are set.
  const previewMilestones = useMemo(() => {
    const fromQuote = scope?.payments || [];
    if (fromQuote.length) {
      return fromQuote.map(p => ({ title: p.name, trigger: p.trigger, amount: p.amount }));
    }
    return (milestones || []).map(m => ({
      title: m.title, trigger: m.due_date ? `Due ${fmtShort(m.due_date)}` : (m.type || ""),
      amount: m.calculated_amount,
    }));
  }, [scope, milestones]);
  const milestoneTotal = previewMilestones.reduce((s, m) => s + (Number(m.amount) || 0), 0);
  const bufferLabel = `+${Math.round(bufferPct * 100)}% WD`;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="timeline-breakdown">
        <DialogHeader>
          <DialogTitle>Timeline from scope</DialogTitle>
          <DialogDescription>
            What will be built. Adjust the artists and prerequisites — every figure recalculates.
          </DialogDescription>
        </DialogHeader>

        {/* Artists + live totals */}
        <div className="flex items-center gap-3 rounded-xl border border-border px-3.5 py-2.5">
          <Label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground whitespace-nowrap">
            Artists
          </Label>
          <Input
            type="number" min={1} max={20} value={artists}
            onChange={(e) => setArtists(e.target.value)}
            className="h-9 w-16 text-center text-base font-semibold tabular-nums"
            data-testid="bd-artists"
          />
          <div className="flex-1 h-px bg-border" />
          {preview && (
            <div className="flex items-center gap-2" data-testid="bd-production-weeks">
              <span className="text-xl font-semibold tabular-nums" style={{ color: KICKOFF_COLOR }}>
                {preview.production_weeks}
              </span>
              <span className="text-[12px] text-muted-foreground">production wks</span>
              <span className="rounded px-2 py-0.5 text-[11px] font-semibold font-mono" style={{ background: "rgba(94,142,255,.12)", color: KICKOFF_COLOR }}>
                {preview.size}
              </span>
              <span className="text-[11px] font-mono text-muted-foreground">{preview.end_date}</span>
            </div>
          )}
          {previewing && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-[#C24A2E]/30 bg-[#C24A2E]/[0.06] px-3 py-2 text-[12px] text-[#C24A2E]">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {String(error)}
          </div>
        )}

        {preview && (
          <>
            {/* Scope breakdown */}
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="border-b border-border px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Scope Breakdown
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse" data-testid="bd-scope-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-2.5 py-1.5 text-left text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Category</th>
                      <th className="px-2.5 py-1.5 text-center text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Qty</th>
                      <th className="px-2.5 py-1.5 text-right text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Raw days</th>
                      <th className="px-2.5 py-1.5 text-right text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{bufferLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.scope_rows.map((r, i) => (
                      <tr key={r.category} className={`border-b border-border ${i % 2 ? "bg-secondary/30" : ""}`}>
                        <td className="px-2.5 py-1.5 text-[11px] font-medium">{r.label}</td>
                        <td className="px-2.5 py-1.5 text-center text-[11px] text-muted-foreground">
                          {r.by_hand ? "set by hand" : `${fmtNum(r.qty)} ${r.unit}`}
                        </td>
                        <td className="px-2.5 py-1.5 text-right text-[11px] font-mono text-muted-foreground">{fmtNum(r.raw_days)}d</td>
                        <td className="px-2.5 py-1.5 text-right text-[11px] font-mono font-bold" style={{ color: KICKOFF_COLOR }}>
                          {r.working_days}wd
                        </td>
                      </tr>
                    ))}
                    {preview.scope_rows.length === 0 && (
                      <tr><td colSpan={4} className="px-2.5 py-3 text-center text-[12px] text-muted-foreground">
                        Nothing to schedule — set some quantities or modeling days.
                      </td></tr>
                    )}
                    <tr className="border-t-2 border-border bg-secondary/40">
                      <td colSpan={3} className="px-2.5 py-2 text-[12px] font-bold">TOTAL</td>
                      <td className="px-2.5 py-2 text-right text-[13px] font-bold font-mono" style={{ color: KICKOFF_COLOR }}>
                        {preview.total_working_days}wd
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Total project — prominent, includes the prereqs */}
              <div
                className="flex items-center justify-between px-4 py-3 border-t"
                style={{ background: "rgba(94,142,255,.08)", borderColor: "rgba(94,142,255,.3)" }}
                data-testid="bd-total-project"
              >
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider mb-0.5" style={{ color: KICKOFF_COLOR, opacity: .75 }}>
                    Total project (incl. prereqs)
                  </div>
                  <div className="text-[12px] font-mono text-muted-foreground">{preview.end_date}</div>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl font-extrabold leading-none tabular-nums" style={{ color: KICKOFF_COLOR }}>
                    {preview.total_project_weeks}
                  </span>
                  <span className="text-base font-semibold" style={{ color: KICKOFF_COLOR, opacity: .85 }}>weeks</span>
                </div>
              </div>
            </div>

            {/* Prerequisites */}
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="border-b border-border px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Project prerequisites — set before production starts
              </div>
              <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-2">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: KICKOFF_COLOR }} />
                <span className="flex-1 text-[12px] font-semibold">Kickoff / Asset Collection</span>
                <span className="text-[11px] text-muted-foreground">Date</span>
                <Input
                  type="date" value={kickoffDate} onChange={(e) => setKickoffDate(e.target.value)}
                  className="h-8 w-[9.5rem] text-[12px]" data-testid="bd-kickoff-date"
                />
              </div>
              <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-2">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: BUFFER_COLOR }} />
                <span className="flex-1 text-[12px] font-semibold">Buffer</span>
                <span className="text-[11px] text-muted-foreground">Working days</span>
                <Input
                  type="number" min={0} max={90} value={bufferWd} onChange={(e) => setBufferWd(e.target.value)}
                  className="h-8 w-16 text-center text-[13px] font-semibold tabular-nums"
                  style={{ color: BUFFER_COLOR }} data-testid="bd-buffer-wd"
                />
                <span className="w-11 text-right text-[11px] font-mono text-muted-foreground">{preview.buffer_wd} wd</span>
              </div>
              <div className="flex items-center gap-2.5 px-3.5 py-2">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: "#3D6355" }} />
                <span className="flex-1 text-[12px] font-semibold">3D Modeling</span>
                <span className="text-[11px] text-muted-foreground">Working days</span>
                <Input
                  type="number" min={0} max={400} value={modelingWd} onChange={(e) => setModelingWd(e.target.value)}
                  className="h-8 w-16 text-center text-[13px] font-semibold tabular-nums"
                  style={{ color: "#3D6355" }} data-testid="bd-modeling-wd"
                />
                <span className="w-11 text-right text-[11px] font-mono text-muted-foreground">{preview.modeling_wd} wd</span>
              </div>
            </div>

            {/* Production phases */}
            <div>
              <div className="mb-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Production phases — <span className="normal-case font-sans">click a phase to expand deliverables</span>
              </div>
              <div className="space-y-1.5" data-testid="bd-phases">
                {preview.sections.length === 0 ? (
                  <p className="text-[12px] text-muted-foreground">No production phases from this scope yet.</p>
                ) : preview.sections.map(sec => (
                  <div key={sec.id} className="rounded-lg border border-border overflow-hidden">
                    <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-3 py-2">
                      <span className="w-[3px] h-4 rounded-sm shrink-0" style={{ background: sec.color }} />
                      <span className="text-[11px] font-bold uppercase tracking-widest">{sec.label}</span>
                      <span className="ml-auto text-[10px] font-mono text-muted-foreground">{sec.working_days} wd total</span>
                    </div>
                    {sec.phases.map(ph => {
                      const key = `${sec.id}:${ph.name}`;
                      const isOpen = !!expanded[key];
                      const itemCount = ph.items.reduce((s, i) => s + i.qty, 0);
                      return (
                        <div key={key} className="border-b border-border last:border-b-0">
                          <button
                            className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-secondary/40"
                            onClick={() => setExpanded(e => ({ ...e, [key]: !isOpen }))}
                            data-testid={`bd-phase-${ph.name.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-sm shrink-0" style={{ background: ph.color }} />
                            <span className="flex-1 text-[12px] font-medium">{ph.name}</span>
                            {itemCount > 0 && <span className="text-[10px] text-muted-foreground">{fmtNum(itemCount)} items</span>}
                            <span className="text-[10px] font-mono text-muted-foreground">{ph.working_days} wd</span>
                            {ph.items.length > 0 && (
                              <ChevronRight className={`w-3 h-3 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`} />
                            )}
                          </button>
                          {isOpen && ph.items.map(item => (
                            <div key={item.key} className="flex items-center justify-between border-t border-border bg-secondary/20 py-1 pl-8 pr-3">
                              <span className="text-[11px] text-muted-foreground">{fmtNum(item.qty)} × {item.label}</span>
                              <span className="text-[10px] font-mono text-muted-foreground">
                                {fmtNum(item.raw_days)}d raw · {item.working_days}wd
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Payment milestones */}
            {previewMilestones.length > 0 && (
              <div>
                <div className="mb-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  Payment milestones
                </div>
                <div className="space-y-1" data-testid="bd-milestones">
                  {previewMilestones.map((m, i) => (
                    <div key={i} className="flex items-center gap-2.5 rounded-md border border-border px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold truncate">{m.title || "Milestone"}</div>
                        {m.trigger && <div className="text-[10px] text-muted-foreground mt-0.5">{m.trigger}</div>}
                      </div>
                      <div className="text-[14px] font-bold" style={{ color: "#2E7D55" }}>{fmtMoney(m.amount)}</div>
                    </div>
                  ))}
                  <div className="flex justify-end px-2 pt-1 text-[11px] text-muted-foreground">
                    Total milestones: <b className="ml-1 text-foreground">{fmtMoney(milestoneTotal)}</b>
                  </div>
                </div>
              </div>
            )}

            {preview.existing_phase_count > 0 && (
              <div className="flex items-start gap-2 rounded-lg border border-[#B96A43]/30 bg-[#B96A43]/[0.06] px-3 py-2 text-[11px] text-muted-foreground">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#B96A43]" />
                This project already has {preview.existing_phase_count} production phase
                {preview.existing_phase_count === 1 ? "" : "s"} — updating replaces the whole schedule.
              </div>
            )}
          </>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={onBack} data-testid="bd-back">Back to scope</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => apply(false)} disabled={applying || previewing || !preview} data-testid="bd-apply">
              {applying && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Update Project
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Project Timeline — read-only view of the same phases the Production
// Timeline edits, one row per phase (Kickoff, Buffer, 3D Modeling, the render
// phases), plus a Milestones row from the program's real payment schedule. ──
function ProjectTimelineGantt({ phases, sections, milestones, artists, loading, canViewFinancials, onShift }) {
  const [zoom, setZoom] = useState(8);
  const [shifting, setShifting] = useState(false);
  const [msTooltip, setMsTooltip] = useState(null); // { m, left }

  const rows = useMemo(() => {
    // Fixed track order (Kickoff/Buffer → Modeling → Exterior → Interior →
    // Vexos), dates only breaking ties within a track — a phase dragged
    // earlier than another track's shouldn't reshuffle the whole list.
    const rankBySection = new Map((sections || []).map(s => [s.id, sectionRank(s)]));
    const phaseRank = (p) => {
      const tag = (p.deliverable_tag || "").toLowerCase();
      if (tag === "kickoff") return -2;
      if (tag === "buffer") return -1;
      if (p.section_id && rankBySection.has(p.section_id)) return rankBySection.get(p.section_id);
      return 90;
    };
    const ordered = [...phases].sort((a, b) => {
      const ra = phaseRank(a), rb = phaseRank(b);
      if (ra !== rb) return ra - rb;
      const ta = new Date(a.start_date).getTime(), tb = new Date(b.start_date).getTime();
      if (ta !== tb) return ta - tb;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
    return ordered.map(p => ({
      key: p.id,
      name: p.name,
      color: p.color || "#3D6355",
      start: new Date(p.start_date),
      end: addDays(p.start_date, Math.max(1, p.duration_days || 1)),
      isKickoffTagged: (p.deliverable_tag || "").toLowerCase() === "kickoff",
      isKickoff: (p.deliverable_tag || "").toLowerCase() === "kickoff" || (p.duration_days || 1) <= 1,
      done: !!p.is_complete,
    })).filter(r => !isNaN(r.start.getTime()));
  }, [phases, sections]);

  // The kickoff date drives the whole schedule. Prefer the phase actually
  // tagged kickoff — falling back to the earliest bar would follow a phase
  // someone dragged in front of it.
  const kickoffDate = useMemo(() => {
    if (!rows.length) return "";
    const tagged = rows.find(r => r.isKickoffTagged);
    return isoDate((tagged || rows[0]).start);
  }, [rows]);

  const { rangeStart, totalDays } = useMemo(() => {
    // The range START comes from the phases alone. A program can carry payment
    // dates from long before its production window (a stale schedule, a
    // historical deposit), and letting an outlier pull the start back squeezes
    // the real schedule into an unreadable sliver. The END does extend to cover
    // later milestones, which is how the planner's own Gantt read.
    let min = Infinity, max = -Infinity;
    rows.forEach(r => {
      const s = r.start.getTime(), e = r.end.getTime();
      if (s < min) min = s;
      if (e > max) max = e;
    });
    if (min === Infinity) { min = Date.now() - 7 * DAY_MS; max = Date.now() + 60 * DAY_MS; }
    (milestones || []).forEach(m => {
      const t = m.due_date ? new Date(m.due_date).getTime() : NaN;
      // Cap the stretch so one far-future date can't flatten the schedule.
      if (!isNaN(t) && t > max && t - max <= 180 * DAY_MS) max = t;
    });
    const startMs = utcDayStart(min - 3 * DAY_MS);
    const endMs = max + 7 * DAY_MS;
    const days = Math.max(30, Math.ceil((endMs - startMs) / DAY_MS));
    return { rangeStart: new Date(startMs), totalDays: days };
  }, [rows, milestones]);

  const px = zoom;
  const scale = useMemo(
    () => utcRulerScale(rangeStart.getTime(), totalDays, px),
    [rangeStart, totalDays, px]
  );
  const LABEL_W = 170;
  const totalWidth = totalDays * px;
  const rangeEndMs = rangeStart.getTime() + totalDays * DAY_MS;
  const offsetFor = (d) => Math.floor((new Date(d).getTime() - rangeStart.getTime()) / DAY_MS) * px;
  const todayOffset = offsetFor(new Date());
  const todayVisible = Date.now() >= rangeStart.getTime() && Date.now() <= rangeEndMs;

  const visibleMilestones = useMemo(
    () => (milestones || []).filter(m => {
      if (!m.due_date) return false;
      const t = new Date(m.due_date).getTime();
      return !isNaN(t) && t >= rangeStart.getTime() && t <= rangeEndMs;
    }),
    [milestones, rangeStart, rangeEndMs]
  );
  const hiddenMilestoneCount = (milestones || []).filter(m => m.due_date).length - visibleMilestones.length;

  async function changeKickoff(value) {
    if (!value || !kickoffDate) return;
    const delta = Math.round((new Date(value).getTime() - new Date(kickoffDate).getTime()) / DAY_MS);
    if (!delta) return;
    setShifting(true);
    try { await onShift(delta); toast.success("Timeline shifted"); }
    catch { toast.error("Couldn't shift the timeline"); }
    finally { setShifting(false); }
  }

  if (loading) return null;

  const ROW_H = 28;
  const milestoneRowTop = 8 + rows.length * ROW_H;

  return (
    <ProgramSection
      title="Project Timeline"
      action={
        <div className="flex items-center gap-2">
          {artists > 0 && (
            <span
              className="rounded px-2 py-0.5 text-[10px] font-mono font-semibold"
              style={{ background: "rgba(94,142,255,.12)", color: KICKOFF_COLOR }}
              data-testid="pt-artist-badge"
            >
              {artists} artist{artists > 1 ? "s" : ""}
            </span>
          )}
          {rows.length > 0 && (
            <label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-muted-foreground">
              Kickoff
              {shifting
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Input
                    type="date" value={kickoffDate} onChange={(e) => changeKickoff(e.target.value)}
                    className="h-7 w-[8.75rem] text-[11px]" title="Shifts the whole timeline"
                    data-testid="pt-kickoff-date"
                  />}
            </label>
          )}
          <div className="flex items-center gap-0.5 rounded-full border border-border px-1">
            <button onClick={() => setZoom(z => Math.max(4, z - 2))} className="h-7 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground" data-testid="pt-zoom-out"><ZoomOut className="w-3.5 h-3.5" /></button>
            <span className="w-4 text-center text-[11px] font-mono tabular-nums text-muted-foreground">{zoom}</span>
            <button onClick={() => setZoom(z => Math.min(28, z + 2))} className="h-7 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground" data-testid="pt-zoom-in"><ZoomIn className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      }
    >
      {rows.length === 0 ? (
        <p className="py-2 text-[13px] text-muted-foreground">
          No timeline yet — use Parse Agreement above to build one.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border" data-testid="project-timeline">
          <div style={{ width: LABEL_W + totalWidth, position: "relative" }}>
            {/* Ruler */}
            <div className="grid border-b border-border" style={{ gridTemplateColumns: `${LABEL_W}px 1fr`, height: RULER_H }}>
              <div className="flex items-center border-r border-border pl-2.5 text-[10px] font-mono tracking-wide text-muted-foreground">DATE</div>
              <GanttRuler scale={scale} px={px} todayOffset={todayOffset} todayVisible={todayVisible} />
            </div>
            {/* Rows */}
            <div className="grid" style={{ gridTemplateColumns: `${LABEL_W}px 1fr` }}>
              <div className="border-r border-border">
                {rows.map(r => (
                  <div key={r.key} className="flex items-center gap-2 px-2.5" style={{ height: ROW_H }}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.color }} />
                    <span className={`truncate text-[12px] ${r.done ? "text-muted-foreground line-through" : ""}`} title={r.name}>{r.name}</span>
                  </div>
                ))}
                {visibleMilestones.length > 0 && (
                  <div className="flex items-center gap-2 border-t border-border px-2.5" style={{ height: ROW_H }}>
                    <Diamond className="w-2.5 h-2.5 shrink-0 text-muted-foreground" />
                    <span className="text-[11px] text-muted-foreground">Milestones</span>
                  </div>
                )}
              </div>
              <div className="relative" style={{ height: rows.length * ROW_H + (visibleMilestones.length ? ROW_H + 1 : 0) }}>
                <GanttGridlines scale={scale} px={px} />
                {todayVisible && (
                  <div className="absolute top-0 bottom-0 w-px" style={{ left: todayOffset, background: "rgba(194,74,46,.35)" }} />
                )}
                {rows.map((r, idx) => {
                  const left = offsetFor(r.start);
                  const width = Math.max(px, Math.round((r.end.getTime() - r.start.getTime()) / DAY_MS) * px);
                  const title = `${r.name} · ${fmtShort(r.start)} → ${fmtShort(addDays(r.end, -1))}`;
                  if (r.isKickoff) {
                    // A single-day marker reads as a diamond, not a sliver of bar.
                    return (
                      <div key={r.key} className="absolute rotate-45 rounded-[2px]" title={title}
                        style={{ left: left - 5, top: (ROW_H - 10) / 2 + idx * ROW_H, width: 10, height: 10, background: r.color, opacity: r.done ? 0.45 : 1 }} />
                    );
                  }
                  return (
                    <div key={r.key} className="absolute" style={{ left, top: 4 + idx * ROW_H, width, height: ROW_H - 8 }}>
                      <div className="flex h-full items-center truncate rounded-md px-2 text-[11px] font-medium text-white"
                        style={{ background: r.color, opacity: r.done ? 0.45 : 1 }} title={title}>
                        {r.name}
                      </div>
                    </div>
                  );
                })}
                {visibleMilestones.map((m, i) => {
                  const overdue = (m.status || "").toLowerCase() !== "paid" && new Date(m.due_date) < new Date();
                  const paid = (m.status || "").toLowerCase() === "paid";
                  return (
                    <div
                      key={m.id || i}
                      className="absolute rotate-45 cursor-default rounded-[2px] border border-black/20 transition-transform hover:scale-125"
                      style={{
                        left: offsetFor(m.due_date) - 6, top: milestoneRowTop + (ROW_H - 12) / 2,
                        width: 12, height: 12,
                        background: paid ? "#2E7D55" : overdue ? "#C24A2E" : "#B96A43",
                      }}
                      onMouseEnter={() => setMsTooltip({ m, left: offsetFor(m.due_date) })}
                      onMouseLeave={() => setMsTooltip(null)}
                      data-testid={`pt-milestone-${m.id || i}`}
                    />
                  );
                })}
                {msTooltip && (() => {
                  const m = msTooltip.m;
                  // amount_value alone is only a dollar figure when the type is
                  // Fixed — for percent milestones it's the percentage.
                  const amount = m.calculated_amount != null
                    ? m.calculated_amount
                    : (m.amount_type === "Fixed" ? m.amount_value : null);
                  const status = (m.status || "").toLowerCase();
                  return (
                    <div
                      className="pointer-events-none absolute z-20 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-popover-foreground shadow-md"
                      style={{ left: msTooltip.left, top: Math.max(2, milestoneRowTop - 40) }}
                      data-testid="pt-milestone-tooltip"
                    >
                      <div className="text-[11px] font-semibold">
                        {m.title || "Milestone"}
                        {canViewFinancials && amount != null && ` — ${fmtMoney(amount)}`}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        Due {fmtShort(m.due_date)}{status ? ` · ${m.status}` : ""}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
      {hiddenMilestoneCount > 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {hiddenMilestoneCount} milestone{hiddenMilestoneCount === 1 ? "" : "s"} fall outside this window — see Payments below.
        </p>
      )}
    </ProgramSection>
  );
}

// ── Production Timeline — grouped into sections, each phase editable
// (drag, resize, rename, tag, assignee, done, delete). ───────────────────
function ProductionTimelinePanel({ programId, phases, sections, loading, onChange, onRefresh, canRecalc, onRecalc }) {
  const [zoom, setZoom] = useState(8);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [editDatesOpen, setEditDatesOpen] = useState(false);

  // Kickoff/Buffer and any phase that's lost its section (the backend
  // deliberately detaches rather than cascade-deletes, "don't lose work") have
  // no section_id. Both need their own group so they're still visible and
  // editable here instead of quietly disappearing from the one place phases
  // can be worked on.
  const grouped = useMemo(() => {
    const bySec = new Map(sections.map(s => [s.id, { section: s, phases: [] }]));
    const prereq = [];
    const unsectioned = [];
    for (const p of phases) {
      if (p.section_id && bySec.has(p.section_id)) bySec.get(p.section_id).phases.push(p);
      else if (["buffer", "kickoff"].includes((p.deliverable_tag || "").toLowerCase())) prereq.push(p);
      else if (!p.section_id) unsectioned.push(p);
    }
    const out = [];
    if (prereq.length) out.push({ section: { id: "__prereq", label: "Kickoff & Buffer", isSpecial: "prereq" }, phases: prereq });
    // Real DB sections always show, even freshly created with zero phases —
    // otherwise "Add Section" makes something you can never see or add a phase
    // to. Only the synthetic groups above are conditional, since those aren't
    // real records to begin with.
    out.push(...Array.from(bySec.values()).filter(g => g.section.enabled !== false));
    if (unsectioned.length) out.push({ section: { id: "__unsectioned", label: "Unsectioned", isSpecial: "unsectioned" }, phases: unsectioned });
    return out;
  }, [phases, sections]);

  const { rangeStart, totalDays } = useMemo(() => {
    let min = Infinity, max = -Infinity;
    phases.forEach(p => {
      const s = new Date(p.start_date).getTime();
      const e = s + (p.duration_days || 1) * DAY_MS;
      if (!isNaN(s)) { if (s < min) min = s; if (e > max) max = e; }
    });
    if (min === Infinity) { min = Date.now() - 7 * DAY_MS; max = Date.now() + 60 * DAY_MS; }
    const startMs = utcDayStart(min - 5 * DAY_MS);
    const endMs = max + 14 * DAY_MS;
    const days = Math.max(45, Math.ceil((endMs - startMs) / DAY_MS));
    return { rangeStart: new Date(startMs), totalDays: days };
  }, [phases]);

  const px = zoom;
  const scale = useMemo(
    () => utcRulerScale(rangeStart.getTime(), totalDays, px),
    [rangeStart, totalDays, px]
  );
  const totalWidth = totalDays * px;
  const todayOffset = Math.floor((Date.now() - rangeStart.getTime()) / DAY_MS) * px;
  const todayVisible = Date.now() >= rangeStart.getTime() && Date.now() <= rangeStart.getTime() + totalDays * DAY_MS;
  const offsetFor = useCallback((dateStr) => {
    if (!dateStr) return null;
    const t = new Date(dateStr).getTime();
    if (isNaN(t)) return null;
    return Math.floor((t - rangeStart.getTime()) / DAY_MS) * px;
  }, [rangeStart, px]);

  const dragRef = useRef(null);
  function startDrag(phase, mode, e) {
    e.stopPropagation();
    dragRef.current = { phase, mode, startX: e.clientX, origStart: new Date(phase.start_date).getTime(), origDur: phase.duration_days };
    const onMove = (ev) => {
      const d = dragRef.current; if (!d) return;
      const deltaDays = Math.round((ev.clientX - d.startX) / px);
      onChange(prev => prev.map(p => {
        if (p.id !== d.phase.id) return p;
        const updated = { ...p };
        if (d.mode === "move") updated.start_date = isoDate(d.origStart + deltaDays * DAY_MS);
        else if (d.mode === "resize-end") updated.duration_days = Math.max(1, d.origDur + deltaDays);
        else if (d.mode === "resize-start") {
          updated.start_date = isoDate(d.origStart + deltaDays * DAY_MS);
          updated.duration_days = Math.max(1, d.origDur - deltaDays);
        }
        return updated;
      }));
    };
    const onUp = async () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const d = dragRef.current; if (!d) return;
      dragRef.current = null;
      onChange(prev => {
        const final = prev.find(p => p.id === d.phase.id);
        if (final && (final.start_date !== isoDate(d.origStart) || final.duration_days !== d.origDur)) {
          apiClient.patch(`/pcc/phases/${d.phase.id}`, { start_date: final.start_date, duration_days: final.duration_days })
            .catch(() => { toast.error("Failed to save — reloading"); onRefresh(); });
        }
        return prev;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  async function createSection(label, trackType) {
    try {
      await apiClient.post(`/pcc/programs/${programId}/sections`, {
        program_id: programId, label, track_type: trackType, enabled: true, sort_order: sections.length,
      });
      onRefresh();
    } catch { toast.error("Failed to add section"); }
  }
  async function deleteSection(section) {
    if (!window.confirm(`Delete "${section.label}"? Its phases stay — they'll move to Unsectioned.`)) return;
    try {
      await apiClient.delete(`/pcc/sections/${section.id}`);
      onRefresh();
    } catch { toast.error("Failed to delete section"); }
  }

  async function addPhase(sectionId) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const siblingCount = phases.filter(p => p.section_id === sectionId).length;
    const body = {
      program_id: programId, section_id: sectionId, name: "New Phase",
      start_date: isoDate(today), duration_days: 5,
      color: PHASE_COLORS[siblingCount % PHASE_COLORS.length], sort_order: phases.length,
    };
    try {
      const res = await apiClient.post(`/pcc/programs/${programId}/phases`, body);
      onChange(prev => [...prev, res.data]);
    } catch { toast.error("Failed to add phase"); }
  }
  async function patchPhase(phase, body) {
    try {
      const res = await apiClient.patch(`/pcc/phases/${phase.id}`, body);
      onChange(prev => prev.map(p => p.id === phase.id ? res.data : p));
    } catch { toast.error("Failed"); }
  }
  async function deletePhase(phase) {
    if (!window.confirm(`Delete "${phase.name}"?`)) return;
    try {
      await apiClient.delete(`/pcc/phases/${phase.id}`);
      onChange(prev => prev.filter(p => p.id !== phase.id));
    } catch { toast.error("Failed to delete"); }
  }
  async function commitName(phase) {
    const name = editingName.trim();
    setEditingId(null);
    if (!name || name === phase.name) return;
    patchPhase(phase, { name });
  }

  return (
    <ProgramSection
      title="Production Timeline"
      action={
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {canRecalc && (
            <Button variant="ghost" size="sm" onClick={onRecalc} className={sectionActionClass} title="Rebuild the schedule from the contract quantities" data-testid="recalc">
              <RotateCw className="w-3.5 h-3.5 mr-1" /> Recalc
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setManageOpen(true)} className={sectionActionClass} data-testid="manage-sections">
            <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Manage Sections
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditDatesOpen(true)} className={sectionActionClass} data-testid="edit-dates">
            <CalendarClock className="w-3.5 h-3.5 mr-1" /> Edit Dates
          </Button>
          <div className="flex items-center gap-0.5 rounded-full border border-border px-1">
            <button onClick={() => setZoom(z => Math.max(4, z - 2))} className="h-7 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground" data-testid="production-zoom-out"><ZoomOut className="w-3.5 h-3.5" /></button>
            <span className="w-4 text-center text-[11px] font-mono tabular-nums text-muted-foreground">{zoom}</span>
            <button onClick={() => setZoom(z => Math.min(28, z + 2))} className="h-7 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground" data-testid="production-zoom-in"><ZoomIn className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : grouped.length === 0 ? (
        <p className="py-2 text-[13px] text-muted-foreground">
          No production sections yet — use Parse Agreement above to build one, or Add Section to start one by hand.
        </p>
      ) : (
        <div className="space-y-5">
          {grouped.map(({ section, phases: secPhases }) => {
            const done = secPhases.filter(p => p.is_complete).length;
            const pct = secPhases.length ? Math.round((done / secPhases.length) * 100) : 0;
            const special = section.isSpecial;
            return (
              <div key={section.id}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold uppercase tracking-wide">{section.label}</span>
                    {section.track_type && <span className="rounded bg-secondary px-1.5 py-0.5 text-[9px] font-mono uppercase text-muted-foreground">{section.track_type}</span>}
                    {!special && <span className="text-[11px] tabular-nums text-muted-foreground">{done}/{secPhases.length} done · {pct}%</span>}
                    {special === "unsectioned" && <span className="text-[11px] text-[#B96A43]">from a deleted section — reassign or delete</span>}
                  </div>
                  {!special && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => addPhase(section.id)} className={sectionActionClass} data-testid={`add-phase-${section.id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add Phase
                      </Button>
                      <button onClick={() => deleteSection(section)} className="p-1.5 text-muted-foreground hover:text-destructive" title="Delete section" data-testid={`delete-section-${section.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto rounded-xl border border-border">
                  <div style={{ width: totalWidth + PROD_LABEL_W, position: "relative" }}>
                    <div className="grid" style={{ gridTemplateColumns: `${PROD_LABEL_W}px 1fr` }}>
                      <div className="border-r border-border" />
                      <div className="relative border-b border-border" style={{ height: RULER_H }}>
                        <GanttRuler scale={scale} px={px} todayOffset={todayOffset} todayVisible={todayVisible} />
                      </div>
                    </div>
                    {secPhases.map((phase) => {
                      const left = offsetFor(phase.start_date);
                      const width = Math.max(8, (phase.duration_days || 1) * px);
                      const editing = editingId === phase.id;
                      const startLabel = fmtShort(phase.start_date);
                      const endLabel = fmtShort(addDays(phase.start_date, Math.max(0, (phase.duration_days || 1) - 1)));
                      // ~5.4px per char at 9px monospace — enough to keep the end
                      // label clear of the start label on a narrow bar.
                      const startLabelWidth = startLabel.length * 5.4;
                      return (
                        <div key={phase.id} className="grid items-center border-t border-border" style={{ gridTemplateColumns: `${PROD_LABEL_W}px 1fr`, height: 44 }}>
                          <div className="flex min-w-0 items-center gap-2 px-3">
                            <input type="checkbox" checked={!!phase.is_complete} onChange={(e) => patchPhase(phase, { is_complete: e.target.checked })} className="shrink-0 accent-[#3D6355]" data-testid={`phase-done-${phase.id}`} />
                            {editing ? (
                              <input
                                autoFocus value={editingName} onChange={(e) => setEditingName(e.target.value)}
                                onBlur={() => commitName(phase)}
                                onKeyDown={(e) => { if (e.key === "Enter") commitName(phase); if (e.key === "Escape") setEditingId(null); }}
                                className="min-w-0 flex-1 border-b border-border bg-transparent text-[13px] outline-none"
                              />
                            ) : (
                              <button onClick={() => { setEditingId(phase.id); setEditingName(phase.name); }} className="min-w-0 flex-1 truncate text-left text-[13px] hover:underline" title="Click to rename">{phase.name}</button>
                            )}
                            <button onClick={() => deletePhase(phase)} className="shrink-0 text-muted-foreground hover:text-destructive" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="relative h-full">
                            <GanttGridlines scale={scale} px={px} />
                            {todayVisible && <div className="absolute top-0 bottom-0 w-px" style={{ left: todayOffset, background: "rgba(194,74,46,.3)" }} />}
                            {left != null && (
                              <div className="absolute select-none" style={{ left, top: 6, width, height: 32 }}>
                                {/* Per-row date labels — the bars are draggable, so the
                                    exact dates need to be readable without hovering. A
                                    short bar's end label has to clear the start label or
                                    the two overlap into an unreadable smear. */}
                                <span className="absolute -top-0.5 left-0 whitespace-nowrap text-[9px] font-mono font-semibold text-muted-foreground">
                                  {fmtShort(phase.start_date)}
                                </span>
                                {(phase.duration_days || 1) > 1 && (
                                  <span
                                    className="absolute -top-0.5 whitespace-nowrap text-[9px] font-mono font-semibold text-muted-foreground"
                                    style={{ left: Math.max(width + 4, startLabelWidth + 8) }}
                                  >
                                    {endLabel}
                                  </span>
                                )}
                                <div
                                  className="group absolute inset-x-0 bottom-0 h-[22px] cursor-move rounded-md shadow-sm"
                                  style={{ background: phase.color || "#3D6355", opacity: phase.is_complete ? 0.5 : 1 }}
                                  onMouseDown={(e) => { if (e.target.dataset?.resize) return; startDrag(phase, "move", e); }}
                                  title={`${phase.name} · ${fmtShort(phase.start_date)} → ${endLabel} · ${phase.duration_days}d`}
                                  data-testid={`prod-phase-${phase.id}`}
                                >
                                  <div data-resize="start" onMouseDown={(e) => startDrag(phase, "resize-start", e)} className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize rounded-l-md hover:bg-white/30" />
                                  <div data-resize="end" onMouseDown={(e) => startDrag(phase, "resize-end", e)} className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize rounded-r-md hover:bg-white/30" />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {secPhases.length === 0 && (
                      <div className="grid border-t border-border" style={{ gridTemplateColumns: `${PROD_LABEL_W}px 1fr`, height: 40 }}>
                        <div className="flex items-center px-3 text-[12px] text-muted-foreground">No phases yet — click + Add Phase</div>
                        <div />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Clock className="w-3 h-3" /> Drag a bar to move it, drag an edge to resize, click the name to rename.
        </p>
        <Button variant="ghost" size="sm" onClick={() => setAddSectionOpen(true)} className={sectionActionClass} data-testid="add-section">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Section
        </Button>
      </div>
      {addSectionOpen && (
        <AddSectionDialog
          onClose={() => setAddSectionOpen(false)}
          onCreate={(label, trackType) => { setAddSectionOpen(false); createSection(label, trackType); }}
        />
      )}
      {manageOpen && (
        <ManageSectionsDialog
          sections={sections}
          phases={phases}
          onClose={() => setManageOpen(false)}
          onToggle={async (section, enabled) => {
            try {
              await apiClient.patch(`/pcc/sections/${section.id}`, { enabled });
              onRefresh();
            } catch { toast.error("Failed to update section"); }
          }}
        />
      )}
      {editDatesOpen && (
        <EditDatesDialog
          phases={phases}
          sections={sections}
          onClose={() => setEditDatesOpen(false)}
          onSave={async (edits) => {
            try {
              await Promise.all(edits.map(e =>
                apiClient.patch(`/pcc/phases/${e.id}`, { start_date: e.start_date, duration_days: e.duration_days })
              ));
              toast.success(`Updated ${edits.length} phase${edits.length === 1 ? "" : "s"}`);
              setEditDatesOpen(false);
              onRefresh();
            } catch { toast.error("Couldn't save the dates"); }
          }}
        />
      )}
    </ProgramSection>
  );
}

const TRACK_TYPES = [
  { value: "modeling", label: "3D Modeling" },
  { value: "exterior", label: "Exterior" },
  { value: "interior", label: "Interior" },
  { value: "vexos", label: "Animation / Vexos" },
  { value: "custom", label: "Custom" },
];

function AddSectionDialog({ onClose, onCreate }) {
  const [label, setLabel] = useState("");
  const [trackType, setTrackType] = useState("custom");
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="add-section-dialog">
        <DialogHeader>
          <DialogTitle>Add a section</DialogTitle>
          <DialogDescription>For scope that wasn't part of the original parse — a marketing add-on, a revision pass, anything ad hoc.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Name</Label>
            <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Marketing Collateral" data-testid="new-section-label" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Track</Label>
            <Select value={trackType} onValueChange={setTrackType}>
              <SelectTrigger data-testid="new-section-track"><SelectValue /></SelectTrigger>
              <SelectContent>{TRACK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!label.trim()} onClick={() => onCreate(label.trim(), trackType)} data-testid="create-section">Add Section</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Manage Sections — show/hide a track without losing its phases. Uses the
// section's own `enabled` flag, which the backend already stores. ─────────
function ManageSectionsDialog({ sections, phases, onClose, onToggle }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="manage-sections-dialog">
        <DialogHeader>
          <DialogTitle>Manage sections</DialogTitle>
          <DialogDescription>
            Turn sections on or off for this project. A disabled section is hidden from the timeline — its phases are kept.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {sections.length === 0 && (
            <p className="text-[13px] text-muted-foreground">No sections yet.</p>
          )}
          {sections.map(sec => {
            const secPhases = phases.filter(p => p.section_id === sec.id);
            const done = secPhases.filter(p => p.is_complete).length;
            const pct = secPhases.length ? Math.round((done / secPhases.length) * 100) : 0;
            const enabled = sec.enabled !== false;
            return (
              <div key={sec.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                <button
                  onClick={() => onToggle(sec, !enabled)}
                  className="relative h-5 w-9 shrink-0 rounded-full border transition-colors"
                  style={{
                    background: enabled ? "#3D6355" : "transparent",
                    borderColor: enabled ? "#3D6355" : "hsl(var(--border))",
                  }}
                  title={enabled ? "Hide this section" : "Show this section"}
                  data-testid={`toggle-section-${sec.id}`}
                >
                  <span className="absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white transition-all" style={{ left: enabled ? 18 : 2 }} />
                </button>
                <div className="min-w-0 flex-1" style={{ opacity: enabled ? 1 : 0.55 }}>
                  <div className="truncate text-[13px] font-semibold uppercase tracking-wide">{sec.label}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                    {secPhases.length} phase{secPhases.length === 1 ? "" : "s"} · {done} done · {pct}%
                  </div>
                </div>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-mono ${enabled ? "text-[#2E7D55]" : "text-muted-foreground"}`}
                  style={{ background: enabled ? "rgba(46,125,85,.12)" : "hsl(var(--secondary))" }}>
                  {enabled ? "ON" : "OFF"}
                </span>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Dates — exact start/duration per phase. Dragging a bar is quick but
// imprecise; this is how a PM sets a date to the day. ────────────────────
function EditDatesDialog({ phases, sections, onClose, onSave }) {
  const sectionLabel = useMemo(() => {
    const m = new Map(sections.map(s => [s.id, s.label]));
    return (p) => p.section_id ? (m.get(p.section_id) || "Unsectioned") : "Kickoff & Buffer";
  }, [sections]);

  const [draft, setDraft] = useState(() =>
    Object.fromEntries(phases.map(p => [p.id, { start_date: (p.start_date || "").slice(0, 10), duration_days: p.duration_days || 1 }]))
  );
  const [saving, setSaving] = useState(false);

  const changed = phases.filter(p => {
    const d = draft[p.id];
    if (!d) return false;
    return d.start_date !== (p.start_date || "").slice(0, 10) || Number(d.duration_days) !== p.duration_days;
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="edit-dates-dialog">
        <DialogHeader>
          <DialogTitle>Edit dates</DialogTitle>
          <DialogDescription>Set exact start dates and durations. Only changed rows are saved.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          {phases.length === 0 && <p className="text-[13px] text-muted-foreground">No phases to edit yet.</p>}
          {phases.map(p => {
            const d = draft[p.id] || { start_date: "", duration_days: 1 };
            const end = d.start_date ? fmtShort(addDays(d.start_date, Math.max(0, Number(d.duration_days) - 1))) : "";
            return (
              <div key={p.id} className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-2">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color || "#3D6355" }} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] font-medium">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground">{sectionLabel(p)}</div>
                </div>
                <Input
                  type="date" value={d.start_date} className="h-8 w-[9.25rem] text-[12px]"
                  onChange={(e) => setDraft(s => ({ ...s, [p.id]: { ...s[p.id], start_date: e.target.value } }))}
                  data-testid={`edit-date-start-${p.id}`}
                />
                <Input
                  type="number" min={1} value={d.duration_days} className="h-8 w-14 text-center text-[12px] tabular-nums"
                  onChange={(e) => setDraft(s => ({ ...s, [p.id]: { ...s[p.id], duration_days: Math.max(1, Number(e.target.value) || 1) } }))}
                  data-testid={`edit-date-dur-${p.id}`}
                />
                <span className="w-14 shrink-0 text-right text-[10px] font-mono text-muted-foreground">{end}</span>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={saving || changed.length === 0}
            onClick={async () => {
              setSaving(true);
              try {
                await onSave(changed.map(p => ({ id: p.id, ...draft[p.id], duration_days: Number(draft[p.id].duration_days) })));
              } finally { setSaving(false); }
            }}
            data-testid="save-dates"
          >
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Save {changed.length > 0 ? `${changed.length} change${changed.length === 1 ? "" : "s"}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

